import Loader from 'components/Loader';
import ContactPageAdd from 'pages/contacts/ContactPageAdd';
import ContractPageAdd from 'pages/contracts/ContractPageAdd';
import CorrespondencePageAdd from 'pages/correspondence/CorrespondencePageAdd';
import EffortPageAdd from 'pages/efforts/EffortPageAdd';
import EmployeePageAdd from 'pages/employees/EmployeePageAdd';
import ExpensePageForm from 'pages/expenses/ExpensePageForm';
import ExperienceRecordPageAdd from 'pages/experience/ExperienceRecordPageAdd';
import GroupPageAdd from 'pages/groups/GroupPageAdd';
import JobPositionPageAdd from 'pages/job-positions/JobPositionPageAdd';
import JobRolePageAdd from 'pages/job-roles/JobRolePageAdd';
import MarketingProfileDetailsPage from 'pages/marketing-profiles/MarketingProfileDetailsPage';
import MarketingProfileDuplicatePage from 'pages/marketing-profiles/MarketingProfileDuplicatePage';
import MatterPageAdd from 'pages/matters/MatterPageAdd';
import MatterPageCopy from 'pages/matters/MatterPageCopy';
import OfficePageAdd from 'pages/offices/OfficePageAdd';
import PracticeAreaPageAdd from 'pages/practice-areas/PracticeAreaPageAdd';
import ProjectPageAdd from 'pages/projects/ProjectPageAdd';
import TaskPageAdd from 'pages/tasks/TaskPageAdd';
import TaskPageDuplicate from 'pages/tasks/TaskPageDuplicate';
import TeamPageAdd from 'pages/teams/TeamPageAdd';
import DuplicateTimecardPage from 'pages/timecards/DuplicateTimecardPage';
import FinishTimecardPageForm from 'pages/timecards/FinishTimecardPageForm';
import TimecardPageForm from 'pages/timecards/TimecardPageForm';
import { createElement, lazy, ReactElement, Suspense } from 'react';
import { matchPath, Params } from 'react-router-dom';
import { FeatureFlag } from 'types/features';
import type { CreateRecordType } from './recordTypes';

const ClientAssetPageAdd = lazy(() => import('pages/client-assets/ClientAssetPageAdd'));
const DebriefingPageAdd = lazy(() => import('pages/debriefing/DebriefingPageAdd'));
const TrainingPageAdd = lazy(() => import('pages/trainings/TrainingPageAdd'));

export type CreateRoute = {
    /** The route's path in `routes/MainRoutes`, from the root. */
    path: string;
    record: CreateRecordType;
    /**
     * What the route renders, without its guards. A guard redirects when it fails, and inside the dialog that
     * would move the page behind it: the fields below say what the guards check instead.
     */
    element: ReactElement;
    /** Any one of these lets the user in. */
    permissions?: string[];
    requiredFeature?: FeatureFlag;
    /** The params that have to be a record id. */
    numericParams?: string[];
    /** On a copy or duplicate route, the param that holds the id of the record it starts from. */
    sourceParam?: string;
    /**
     * The page an old URL of this route lands on, with the dialog open over it: the record's list, or the
     * parent record of a nested route, on its tab of these records where it has one. A pattern over this route's
     * own params.
     */
    behind: string;
};

const CORRESPONDENCE_ACCESS = {
    permissions: [
        'READ.CORRESPONDENCE.ALL',
        'READ.CORRESPONDENCE.MY_CASES',
        'READ.CORRESPONDENCE.CREATED_BY_ME',
        'READ.CORRESPONDENCE.MY_OWN',
    ],
    requiredFeature: FeatureFlag.CORRESPONDENCE_MODULE,
};

const CLIENT_ASSET_ACCESS = { permissions: ['CREATE.PARTY_ASSETS'], requiredFeature: FeatureFlag.PARTY_ASSETS };

const DEBRIEFING_ACCESS = { permissions: ['CREATE.DEBRIEFINGS'], requiredFeature: FeatureFlag.DEBRIEFING_MODULE };

const MARKETING_FEATURE = FeatureFlag.MARKETING_PROFILE_PAGE;

function clientAssetElement() {
    return createElement(Suspense, { fallback: createElement(Loader) }, createElement(ClientAssetPageAdd));
}

function debriefingElement() {
    return createElement(Suspense, { fallback: createElement(Loader) }, createElement(DebriefingPageAdd));
}

function timecardElement(prefillWithCurrentUser: boolean) {
    return createElement(TimecardPageForm, { type: 'add', prefillWithCurrentUser });
}

/**
 * Every route that creates a record: the ones with a `new`, `copy` or `duplicate` segment, and
 * `/timecards/finish`. The delegation wizard, the new-cases list and `/timecards/finish/:id`, which edits a
 * draft, are not creations in a dialog and stay out.
 */
export const CREATE_ROUTES: CreateRoute[] = [
    { path: '/matter/new', behind: '/matters', record: 'case', element: createElement(MatterPageAdd) },
    {
        path: '/contact/:contactId/matter/new',
        behind: '/contact/:contactId',
        record: 'case',
        element: createElement(MatterPageAdd),
        numericParams: ['contactId'],
    },
    {
        path: '/matter/copy/:id',
        behind: '/matters',
        sourceParam: 'id',
        record: 'case',
        element: createElement(MatterPageCopy),
        numericParams: ['id'],
    },

    { path: '/contact/new', behind: '/contacts', record: 'contact', element: createElement(ContactPageAdd) },

    { path: '/timecards/new', behind: '/timecards', record: 'timecard', element: timecardElement(true) },
    {
        path: '/matter/:matterId/timecards/new',
        behind: '/matter/:matterId',
        record: 'timecard',
        element: timecardElement(true),
        numericParams: ['matterId'],
    },
    {
        path: '/contact/:contactId/timecards/new',
        behind: '/contact/:contactId',
        record: 'timecard',
        element: timecardElement(true),
        numericParams: ['contactId'],
    },
    {
        path: '/invoice/:invoiceId/matter/:matterId/assignment/:assignmentId/timecards/new',
        behind: '/invoice/:invoiceId/timecards',
        record: 'timecard',
        element: timecardElement(false),
        numericParams: ['invoiceId', 'matterId', 'assignmentId'],
    },
    {
        path: '/invoice/:invoiceId/matter/:matterId/timecards/new',
        behind: '/invoice/:invoiceId/timecards',
        record: 'timecard',
        element: timecardElement(false),
        numericParams: ['invoiceId', 'matterId'],
    },
    {
        path: '/timecards/finish',
        behind: '/timecards',
        record: 'timecard',
        element: createElement(FinishTimecardPageForm),
    },
    {
        path: '/timecards/duplicate/:id',
        behind: '/timecards',
        sourceParam: 'id',
        record: 'timecard',
        element: createElement(DuplicateTimecardPage),
        numericParams: ['id'],
    },

    {
        path: '/expense/new',
        behind: '/expenses',
        record: 'expense',
        element: createElement(ExpensePageForm),
        permissions: ['CREATE.EXPENSES'],
    },
    {
        path: '/invoice/:invoiceId/matter/:matterId/expenses/new',
        behind: '/invoice/:invoiceId/expenses',
        record: 'expense',
        element: createElement(ExpensePageForm),
        permissions: ['CREATE.EXPENSES'],
        numericParams: ['invoiceId', 'matterId'],
    },

    { path: '/contract/new', behind: '/contracts', record: 'contract', element: createElement(ContractPageAdd) },
    {
        path: '/contact/:contactId/contract/new',
        behind: '/contact/:contactId',
        record: 'contract',
        element: createElement(ContractPageAdd),
        numericParams: ['contactId'],
    },

    {
        path: '/client-asset/new',
        behind: '/client-assets',
        record: 'client-asset',
        element: clientAssetElement(),
        ...CLIENT_ASSET_ACCESS,
    },
    {
        path: '/matter/:matterId/client-asset/new',
        behind: '/matter/:matterId',
        record: 'client-asset',
        element: clientAssetElement(),
        ...CLIENT_ASSET_ACCESS,
        numericParams: ['matterId'],
    },
    {
        path: '/contact/:contactId/client-asset/new',
        behind: '/contact/:contactId',
        record: 'client-asset',
        element: clientAssetElement(),
        ...CLIENT_ASSET_ACCESS,
        numericParams: ['contactId'],
    },

    {
        path: '/correspondence/new',
        behind: '/correspondence',
        record: 'correspondence',
        element: createElement(CorrespondencePageAdd),
        ...CORRESPONDENCE_ACCESS,
    },

    {
        path: '/debriefing/new',
        behind: '/debriefings',
        record: 'debriefing',
        element: debriefingElement(),
        ...DEBRIEFING_ACCESS,
    },
    {
        path: '/matter/:matterId/debriefing/new',
        behind: '/matter/:matterId',
        record: 'debriefing',
        element: debriefingElement(),
        ...DEBRIEFING_ACCESS,
        numericParams: ['matterId'],
    },
    {
        path: '/contact/:contactId/debriefing/new',
        behind: '/contact/:contactId',
        record: 'debriefing',
        element: debriefingElement(),
        ...DEBRIEFING_ACCESS,
        numericParams: ['contactId'],
    },

    {
        path: '/experience-records/new',
        behind: '/experience-records',
        record: 'experience-record',
        element: createElement(ExperienceRecordPageAdd),
    },
    {
        path: '/matter/:matterId/experience-records/new',
        behind: '/matter/:matterId',
        record: 'experience-record',
        element: createElement(ExperienceRecordPageAdd),
        numericParams: ['matterId'],
    },
    {
        path: '/contact/:contactId/experience-records/new',
        behind: '/contact/:contactId',
        record: 'experience-record',
        element: createElement(ExperienceRecordPageAdd),
        numericParams: ['contactId'],
    },

    { path: '/task/new', behind: '/tasks', record: 'task', element: createElement(TaskPageAdd) },
    {
        path: '/task/new/:id',
        behind: '/tasks',
        sourceParam: 'id',
        record: 'task',
        element: createElement(TaskPageDuplicate),
        numericParams: ['id'],
    },
    {
        path: '/contact/:contactId/task/new',
        behind: '/contact/:contactId',
        record: 'task',
        element: createElement(TaskPageAdd),
        numericParams: ['contactId'],
    },
    {
        path: '/matter/:matterId/task/new',
        behind: '/matter/:matterId',
        record: 'task',
        element: createElement(TaskPageAdd),
        numericParams: ['matterId'],
    },
    {
        path: '/correspondence/:correspondenceId/task/new',
        behind: '/correspondence/:correspondenceId',
        record: 'task',
        element: createElement(TaskPageAdd),
        ...CORRESPONDENCE_ACCESS,
        numericParams: ['correspondenceId'],
    },
    {
        path: '/debriefing/:debriefingId/task/new',
        behind: '/debriefing/:debriefingId',
        record: 'task',
        element: createElement(TaskPageAdd),
        permissions: ['CREATE.TASKS'],
        requiredFeature: FeatureFlag.DEBRIEFING_MODULE,
        numericParams: ['debriefingId'],
    },

    { path: '/effort/new', behind: '/efforts', record: 'bd-effort', element: createElement(EffortPageAdd) },
    {
        path: '/matter/:matterId/effort/new',
        behind: '/matter/:matterId',
        record: 'bd-effort',
        element: createElement(EffortPageAdd),
        numericParams: ['matterId'],
    },
    {
        path: '/contact/:contactId/effort/new',
        behind: '/contact/:contactId',
        record: 'bd-effort',
        element: createElement(EffortPageAdd),
        numericParams: ['contactId'],
    },

    { path: '/projects/new', behind: '/projects', record: 'project', element: createElement(ProjectPageAdd) },

    {
        path: '/trainings/new',
        behind: '/trainings',
        record: 'training',
        element: createElement(Suspense, { fallback: createElement(Loader) }, createElement(TrainingPageAdd)),
        permissions: ['CREATE.TRAININGS.ALL'],
        requiredFeature: FeatureFlag.TRAININGS_MODULE,
    },

    {
        path: '/contacts/marketing/new',
        behind: '/contacts/marketing',
        record: 'marketing-profile',
        element: createElement(MarketingProfileDetailsPage),
        permissions: ['CREATE.MARKETING_PROFILE'],
        requiredFeature: MARKETING_FEATURE,
    },
    {
        path: '/contacts/marketing/duplicate/:id',
        behind: '/contacts/marketing',
        sourceParam: 'id',
        record: 'marketing-profile',
        element: createElement(MarketingProfileDuplicatePage),
        permissions: ['READ.MARKETING.ALL', 'CREATE.MARKETING_PROFILE'],
        requiredFeature: MARKETING_FEATURE,
        numericParams: ['id'],
    },

    { path: '/employee/new', behind: '/employees', record: 'employee', element: createElement(EmployeePageAdd) },
    { path: '/group/new', behind: '/groups', record: 'group', element: createElement(GroupPageAdd) },
    { path: '/team/new', behind: '/teams', record: 'team', element: createElement(TeamPageAdd) },
    { path: '/office/new', behind: '/offices', record: 'office', element: createElement(OfficePageAdd) },
    {
        path: '/practice-area/new',
        behind: '/practice-areas',
        record: 'practice-area',
        element: createElement(PracticeAreaPageAdd),
    },
    { path: '/job-role/new', behind: '/job-roles', record: 'job-role', element: createElement(JobRolePageAdd) },
    {
        path: '/admin/job-roles/new',
        behind: '/admin/job-roles',
        record: 'job-role',
        element: createElement(JobRolePageAdd),
    },
    {
        path: '/job-position/new',
        behind: '/job-positions',
        record: 'job-position',
        element: createElement(JobPositionPageAdd),
    },
    {
        path: '/admin/job-positions/new',
        behind: '/admin/job-positions',
        record: 'job-position',
        element: createElement(JobPositionPageAdd),
    },
];

/** The create route a pathname belongs to, with its params, or `null` when it is not a create URL. */
export function matchCreateRoute(pathname: string): { route: CreateRoute; params: Params<string> } | null {
    for (const route of CREATE_ROUTES) {
        const match = matchPath(route.path, pathname);
        if (match) {
            return { route, params: match.params };
        }
    }
    return null;
}
